import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, ButtonGroup, Col, Container, Input, InputGroup, InputGroupText, Row, Table } from "reactstrap";
import { v4 } from "uuid";
import { WEB_FORMS_LOADING } from "../../constants/loading.constant";
import { COLOR_PRIMARY } from "../../constants/theme";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { useWebFormCore } from "../../cores/webForm";
import { useIntl } from "react-intl";
import { ContactInputType } from "../../cores/webForm/constants/InputType.constant";
import { PositionType } from "../../cores/webForm/constants/PositionType.constant";
import FormCard from "../component/forms/web/FormCard";
import FormNameModal from "../component/forms/web/modal/FormNameModal";
import { UpgradePlanButton } from "../component/plan/UpgradePlanButton";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { useGlobalCore } from "../../cores/globals";
import { WEBFORM_CONFIG } from "../../constants/modal.constant";
import { Action } from "../../cores/webForm/constants/Action.constant";
import { FormColumnWidth } from "../../cores/webForm/constants/FormColumnWidth.constant";
import "./WebForms.scss";

type WebFormsViewMode = "grid" | "cards" | "table";

export default function WebForms() {
  const intl = useIntl();
  const { company, companyRole, isAdmin, isReseller, companyPlan } = useMeCore();
  const { turnOnModal, turnOffModal, isModal, getModalVisibility } = useGlobalCore();
  const { webForms, postWebForm, getWebForms, deleteWebForm, copyWebForm } =
    useWebFormCore();
  const { goToEditWebForm, goToSocial } = useRouterCore();
  const [viewMode, setViewMode] = useState<WebFormsViewMode>("grid");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");
  const [sortBy, setSortBy] = useState<"name-asc" | "name-desc">("name-asc");

  useEffect(() => {
    if (
      companyPlan?.rules.webFormsAmount === 0 || (
        !companyRole?.forms &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller
      )
    ) goToSocial();
  }, [companyPlan, goToSocial, companyRole, isAdmin, isReseller]);

  useEffect(() => {
    getWebForms(company?.id!);
  }, [company, getWebForms]);

  useEffect(() => {}, [webForms]);

  const toggleSideModal = useCallback(() => {
    turnOnModal(WEBFORM_CONFIG, true);
  }, [turnOnModal]);

  const handleDeleteWebForm = useCallback(
    async (webForm) => {
      if (!company?.id) return;
      await deleteWebForm(String(company.id), String(webForm.id));
    },
    [company?.id, deleteWebForm]
  );

  const handleEditWebForm = useCallback(
    async (webForm) => {
      goToEditWebForm(`${webForm.id}`);
    },
    [goToEditWebForm]
  );

  const handleCopyWebForm = useCallback(
    async (webForm) => {
      if (!company?.id) return;
      await copyWebForm(String(company.id), String(webForm.id));
    },
    [company?.id, copyWebForm]
  );

  const canCreate = useMemo(
    () => companyPlan && webForms.length < companyPlan?.rules.webFormsAmount,
    [companyPlan, webForms.length]
  );

  const filteredForms = useMemo(() => {
    const query = search.trim().toLowerCase();
    let next = webForms.filter((form) => {
      const matchesSearch =
        !query ||
        `${form.name} ${form.title} ${form.tag || ""}`.toLowerCase().includes(query);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? form.isActive : !form.isActive);
      return matchesSearch && matchesStatus;
    });

    next = [...next].sort((a, b) =>
      sortBy === "name-asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    );
    return next;
  }, [webForms, search, statusFilter, sortBy]);

  const webFormStats = useMemo(() => {
    const total = webForms.length;
    const active = webForms.filter((f) => f.isActive).length;
    const inactive = total - active;
    return { total, active, inactive };
  }, [webForms]);

  const handleCreateWebForm = useCallback(
    async (name) => {
      await postWebForm(
        name,
        "Formulario de Contacto",
        40,
        "#555555",
        COLOR_PRIMARY,
        100,
        PositionType.MIDDLE.id,
        "Enviar",
        "#ffffff",
        15,
        "",
        "Gracias por dejarnos tu mensaje!",
        Action.MESSAGE,
        [
          {
            id: v4(),
            type: ContactInputType.CONTACT_NAME.id,
            placeholder: "Nombre completo",
            required: true,
            columnWidth: FormColumnWidth.HALF,
          },
          {
            id: v4(),
            type: ContactInputType.CONTACT_EMAIL.id,
            placeholder: "Email",
            required: true,
            columnWidth: FormColumnWidth.HALF,
          },
          {
            id: v4(),
            type: ContactInputType.CONTACT_PHONE.id,
            placeholder: "Teléfono",
            required: true,
            columnWidth: FormColumnWidth.FULL,
          },
        ],
        company?.id!
      );
    },
    [postWebForm, company]
  );

  return (
    <Layout>
      <Header2 title="" mb={false} />
      <Content className="web-forms-page web-forms-ai-page">
        <Container fluid>
          <div className="web-forms-ai__panel mb-3">
            <div className="web-forms-ai__panel-top">
              <div className="web-forms-ai__stats">
                <span className="web-forms-ai__chip">{webFormStats.total} formularios</span>
                <span className="web-forms-ai__chip">{webFormStats.active} activos</span>
                <span className="web-forms-ai__chip">{webFormStats.inactive} inactivos</span>
              </div>
              <div className="d-flex align-items-center flex-wrap">
                <ButtonGroup className="web-forms-ai__views mb-1 mr-2">
                  <Button color="secondary" outline={viewMode !== "grid"} onClick={() => setViewMode("grid")}>
                    Grilla
                  </Button>
                  <Button color="secondary" outline={viewMode !== "cards"} onClick={() => setViewMode("cards")}>
                    Tarjetas
                  </Button>
                  <Button color="secondary" outline={viewMode !== "table"} onClick={() => setViewMode("table")}>
                    Tabla
                  </Button>
                </ButtonGroup>
                {!canCreate && <UpgradePlanButton />}
                {canCreate && (
                  <Button type="button" className="web-forms-create-btn mt-1 mb-1" onClick={toggleSideModal}>
                    {intl.formatMessage({ id: "app.advertisements.formWeb" })}
                  </Button>
                )}
              </div>
            </div>
            <Row className="g-2">
              <Col md="6" xs="12">
                <InputGroup>
                  <InputGroupText>
                    <i className="fas fa-search" />
                  </InputGroupText>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar formulario por nombre, titulo o etiqueta"
                  />
                </InputGroup>
              </Col>
              <Col md="3" xs="12">
                <Input
                  type="select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as "all" | "active" | "inactive")}
                >
                  <option value="all">Todos</option>
                  <option value="active">Activos</option>
                  <option value="inactive">Inactivos</option>
                </Input>
              </Col>
              <Col md="3" xs="12">
                <Input
                  type="select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "name-asc" | "name-desc")}
                >
                  <option value="name-asc">Nombre A-Z</option>
                  <option value="name-desc">Nombre Z-A</option>
                </Input>
              </Col>
            </Row>
          </div>

          <UpdateLoadingCard
            loadingId={WEB_FORMS_LOADING}
            text={intl.formatMessage({ id: "app.advertisements.formWebUpdating" })}
          />

          {!filteredForms.length ? (
            <div className="web-forms-ai__empty">
              <span className="web-forms-ai__empty-badge">Asistente IA Formularios</span>
              <h4 className="web-forms-ai__empty-title">
                Todavia no hay formularios web para mostrar.
              </h4>
              <p className="web-forms-ai__empty-text">
                Crea formularios para captar leads desde tu sitio y centralizar respuestas en Wembii.
              </p>
              <div className="web-forms-ai__empty-hints">
                <span className="web-forms-ai__empty-hint">Define campos clave</span>
                <span className="web-forms-ai__empty-hint">Personaliza boton y colores</span>
                <span className="web-forms-ai__empty-hint">Publica y empieza a recibir contactos</span>
              </div>
              {canCreate && (
                <Button type="button" className="web-forms-create-btn mt-2" onClick={toggleSideModal}>
                  Crear formulario
                </Button>
              )}
            </div>
          ) : viewMode === "table" ? (
            <div className="web-forms-ai__table-wrap">
              <Table responsive hover className="web-forms-ai__table mb-0">
                <thead>
                  <tr>
                    <th>Formulario</th>
                    <th>Titulo</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredForms.map((webForm) => (
                    <tr key={webForm.id}>
                      <td>
                        <span className="web-forms-ai__table-name">{webForm.name}</span>
                      </td>
                      <td>{webForm.title || "-"}</td>
                      <td>
                        <span className={`web-forms-ai__status ${webForm.isActive ? "is-active" : "is-inactive"}`}>
                          {webForm.isActive ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td>
                        <Button size="sm" color="secondary" outline className="mr-1" onClick={() => handleEditWebForm(webForm)}>
                          Editar
                        </Button>
                        {canCreate && (
                          <Button size="sm" color="secondary" outline className="mr-1" onClick={() => handleCopyWebForm(webForm)}>
                            Duplicar
                          </Button>
                        )}
                        <Button size="sm" color="danger" outline onClick={() => handleDeleteWebForm(webForm)}>
                          Eliminar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <Row className="web-forms-grid">
              {filteredForms.map((webForm) => (
                <Col
                  lg={viewMode === "grid" ? "3" : "4"}
                  md={viewMode === "grid" ? "4" : "6"}
                  sm="6"
                  xs="12"
                  key={webForm.id}
                  className="mb-4"
                >
                  <FormCard
                    webForm={webForm}
                    canCreate={canCreate!}
                    onEdit={() => handleEditWebForm(webForm)}
                    onDelete={() => handleDeleteWebForm(webForm)}
                    onCopy={() => handleCopyWebForm(webForm)}
                  />
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </Content>
      <SideModal
        active={isModal(WEBFORM_CONFIG)}
        visible={getModalVisibility(WEBFORM_CONFIG)}
        onClose={() => turnOffModal(WEBFORM_CONFIG)}
      >
        <FormNameModal
          onSubmit={(name) => {
            handleCreateWebForm(name);
            turnOffModal(WEBFORM_CONFIG);
          }}
        />
      </SideModal>
    </Layout>
  );
}
